import { AssignedBubble } from '@/components/global/AssignedBubble';
import { label } from '@/components/ui/embedded-resource-table/descriptor';
import type { SharedTableProps } from '@/components/ui/embedded-resource-table/types';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { TableColumnHeader } from '@/components/ui/table-cells/TableColumnHeader';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, formatDate } from '@/lib/utils';
import type { ProjectTask } from '@/types';
import { Link, router } from '@inertiajs/react';
import { FolderOpen } from 'lucide-react';
import { TaskScheduleBadge, TaskStatusBadge } from './task-badges';
import { taskTableDescriptor } from './task-table-descriptor';

interface TaskTableProps extends Partial<SharedTableProps> {
    tasks: ProjectTask[];
}

/**
 * The project-tasks list, built from the shared descriptor like every other
 * resource table: what the task is, where it lives, where it stands and when it
 * is due. A task has no page of its own, so the row opens its project.
 */
export function TaskTable({
    tasks,
    visibleColumns = new Set(taskTableDescriptor.defaultVisibleColumns),
    columnFilters = {},
    columnFilterOptions = {},
    onFilterChange,
    sortColumn,
    sortDirection,
    onSortChange,
    onColumnHide,
}: TaskTableProps) {
    const { t } = useTranslations();

    const columns = taskTableDescriptor.columns
        .filter((column) => visibleColumns.has(column.id))
        .map((column) => ({
            id: column.id,
            label: label(t, column),
            compact: column.compact,
            sortable: Boolean(column.sort),
        }));

    const openProject = (task: ProjectTask) => {
        const id = task.project?.id ?? task.project_id;
        if (id) {
            router.visit(`/projects/${id}`);
        }
    };

    return (
        <div className="bg-card mb-4 rounded-xl border px-4 py-2.5">
            <Table>
                <TableHeader>
                    <TableRow>
                        {columns.map((column) => {
                            const selectedFilters = columnFilters[column.id] || [];

                            return (
                                <TableHead key={column.id} className={cn(column.compact && 'w-px')}>
                                    <TableColumnHeader
                                        label={column.label}
                                        sortDirection={sortColumn === column.id ? sortDirection : null}
                                        onSortAscending={column.sortable ? () => onSortChange?.(column.id, 'asc') : undefined}
                                        onSortDescending={column.sortable ? () => onSortChange?.(column.id, 'desc') : undefined}
                                        onHide={() => onColumnHide?.(column.id)}
                                        filterOptions={columnFilterOptions[column.id] || []}
                                        selectedFilters={selectedFilters}
                                        onFilterChange={(values) => onFilterChange?.(column.id, values)}
                                        isFiltered={selectedFilters.length > 0}
                                    />
                                </TableHead>
                            );
                        })}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {tasks.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="text-muted-foreground py-8 text-center">
                                {t('projects.tables.tasks.empty', 'No tasks found.')}
                            </TableCell>
                        </TableRow>
                    ) : (
                        tasks.map((task) => (
                            <TableRow key={task.id} className="hover:bg-muted/50 cursor-pointer transition-all" onClick={() => openProject(task)}>
                                {columns.map((column) => {
                                    if (column.id === 'name') {
                                        return (
                                            <TableCell key={column.id}>
                                                <div className="px-2 py-1.5 text-sm font-medium">{task.name}</div>
                                            </TableCell>
                                        );
                                    }

                                    if (column.id === 'project') {
                                        return (
                                            <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                {task.project ? (
                                                    <Link
                                                        href={`/projects/${task.project.id}`}
                                                        className="text-muted-foreground hover:bg-accent hover:text-foreground flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors"
                                                    >
                                                        <FolderOpen className="size-4 shrink-0" />
                                                        <span className="min-w-0">
                                                            <span className="block truncate font-medium">{task.project.contract_number}</span>
                                                            <span className="block truncate text-xs">{task.project.contract_name}</span>
                                                        </span>
                                                    </Link>
                                                ) : (
                                                    <div className="text-muted-foreground px-2 text-sm">
                                                        {t('projects.tables.tasks.no_project', 'No project')}
                                                    </div>
                                                )}
                                            </TableCell>
                                        );
                                    }

                                    if (column.id === 'status') {
                                        return (
                                            <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                <TaskStatusBadge status={task.status} />
                                            </TableCell>
                                        );
                                    }

                                    if (column.id === 'start_date' || column.id === 'end_date') {
                                        const value = column.id === 'start_date' ? task.start_date : task.end_date;

                                        return (
                                            <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                <div className="text-sm">{value ? formatDate(value) : '—'}</div>
                                            </TableCell>
                                        );
                                    }

                                    if (column.id === 'schedule') {
                                        return (
                                            <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                <TaskScheduleBadge task={task} />
                                            </TableCell>
                                        );
                                    }

                                    if (column.id === 'assignee') {
                                        return (
                                            <TableCell key={column.id} className="w-px" onClick={(e) => e.stopPropagation()}>
                                                <AssignedBubble person={task.assignee} role={t('projects.tables.tasks.assignee', 'Assignee')} />
                                            </TableCell>
                                        );
                                    }

                                    return null;
                                })}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
    );
}
